/**
 * HubInitiativeTracker — Hub's read-only view of the Initiative Tracker: portfolio timeline,
 * health, and each initiative's own progress/milestones/risks/latest report as an expandable
 * card (same collapsed-by-default, expand-for-detail pattern as HubCategoryOverview's document
 * cards). No create/edit/delete/approve action anywhere — anyone who needs to actually change
 * something follows a "View full initiative" link out to the real Tracker, the same escape
 * hatch HubDocumentPage already uses to send editors to the Document Register.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import Chart from 'react-apexcharts';
import { PageHeader } from '../../components/PageHeader';
import { initiativesApi, reportsApi } from '../../services/trackerApi';
import { peopleApi } from '../../services/api';
import { formatDate, fmtRelative, HEALTH_COLOR, STATUS_COLOR, CATEGORY_COLOR, effectiveHealth, milestoneCompletion } from '../../utils/format';
import type { Initiative, WeeklyReport, Milestone, InitiativeTier } from '../../types/trackerModels';

const HEALTH_HEX: Record<string, string> = {
  'On Track': 'var(--bs-success)',
  'At Risk': 'var(--bs-warning)',
  Blocked: 'var(--bs-danger)',
  'Not Started': 'var(--bs-secondary)',
};

type TierFilter = InitiativeTier | 'All';
const TIER_FILTERS: TierFilter[] = ['Portfolio', 'Program', 'Project', 'All'];

/** Mirrors OverviewPage.tsx's own flattenTierTree/indentLabel exactly - this page duplicates
 *  that chart rather than sharing a component (see the file header), so the nesting logic is
 *  duplicated too, deliberately kept identical rather than drifting. */
function flattenTierTree(roots: Initiative[], childrenOf: Map<string, Initiative[]>): { initiative: Initiative; depth: number }[] {
  const rows: { initiative: Initiative; depth: number }[] = [];
  const visited = new Set<string>();
  const visit = (node: Initiative, depth: number) => {
    if (visited.has(node.initiativeId)) return;
    visited.add(node.initiativeId);
    rows.push({ initiative: node, depth });
    (childrenOf.get(node.initiativeId) || []).forEach((child) => visit(child, depth + 1));
  };
  roots.forEach((r) => visit(r, 0));
  return rows;
}
// The four spaces before the arrow MUST be non-breaking (U+00A0), matching OverviewPage.tsx's
// own copy exactly - a plain ASCII space run gets collapsed by SVG's default text whitespace
// handling, silently flattening every nested row back to depth 0 on the rendered chart. This
// was confirmed live as a real, silent drift between these two "identical" copies once already
// - check with a hex dump (not a visual diff) if this line is ever touched again.
const indentLabel = (name: string, depth: number): string => (depth === 0 ? name : `${'    '.repeat(depth)}↳ ${name}`);
// np-tone-* uses "neutral" where the old Bootstrap colour name was "secondary".
const toneOf = (color: string): string => (color === 'secondary' ? 'neutral' : color);
const RISK_SEVERITY_TONE: Record<string, string> = { Critical: 'danger', High: 'danger', Medium: 'warning', Low: 'neutral' };
const EXCEPTION_STATUS_TONE: Record<string, string> = { Approved: 'success', Rejected: 'danger', Pending: 'warning' };

export const HubInitiativeTracker: React.FC = () => {
  const { data: initiatives = [], isLoading: initiativesLoading, error } = useQuery<Initiative[]>({
    queryKey: ['initiatives'], queryFn: initiativesApi.getAll, staleTime: 0,
  });
  const { data: people = [] } = useQuery<{ personId: string; personName: string }[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const personName = (id: string) => people.find((p) => p.personId === id)?.personName || 'Unassigned';

  const reportQueries = useQueries({
    queries: initiatives.map((i) => ({
      queryKey: ['reports', i.initiativeId],
      queryFn: () => reportsApi.list(i.initiativeId),
      enabled: !!i.initiativeId,
    })),
  });
  // Plain per-render computation (same convention as CatalogPage.tsx's readingsByMetric) rather
  // than useMemo — cheap for a handful of initiatives, and avoids depending on useQueries' own
  // freshly-allocated-every-render result array.
  const reportsByInitiative: Record<string, WeeklyReport[]> = {};
  initiatives.forEach((init, idx) => {
    reportsByInitiative[init.initiativeId] = ((reportQueries[idx]?.data as WeeklyReport[]) || [])
      .slice()
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  });
  // reportQueries only starts populating once `initiatives` has resolved (it's built by mapping
  // over that array), so initiativesLoading alone would flip false and briefly render every
  // initiative as "no reports yet" while its report fetch is still in flight.
  const reportsLoading = reportQueries.some((q) => q.isLoading);
  const isLoading = initiativesLoading || reportsLoading;

  const [groupBy, setGroupBy] = useState<'category' | 'owner'>('category');
  const [tierFilter, setTierFilter] = useState<TierFilter>('All');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Frozen at mount rather than read fresh on every render - a chart annotation doesn't need
  // sub-day precision, and Date.now() is an impure call this codebase's lint rules reject inside
  // the render body.
  const [todayTs] = useState(() => Date.now());

  // BAU initiatives (ongoing standing work, no real end date) are kept out of the
  // project-shaped stat tiles and timeline chart, same treatment as OverviewPage.tsx - except a
  // BAU item that's been given a real tier/parentInitiativeId nests under its parent instead of
  // vanishing from it. Untiered BAU keeps its own list further down, same as always.
  const projectInitiatives = initiatives.filter((i) => !i.isBau || i.parentInitiativeId);
  const bauInitiatives = initiatives.filter((i) => i.isBau && !i.parentInitiativeId);

  const active = projectInitiatives.filter((i) => i.status !== 'Completed' && i.status !== 'Cancelled');
  const onTrack = active.filter((i) => effectiveHealth(i) === 'On Track').length;
  const atRisk = active.filter((i) => { const h = effectiveHealth(i); return h === 'At Risk' || h === 'Blocked'; }).length;
  const notStarted = active.filter((i) => effectiveHealth(i) === 'Not Started').length;

  const timelineDated = useMemo(() => active.filter((i) => i.startDate && i.currentProjectedEndDate), [active]);
  const timelineDatedIds = useMemo(() => new Set(timelineDated.map((i) => i.initiativeId)), [timelineDated]);
  const timelineChildrenOf = useMemo(() => {
    const map = new Map<string, Initiative[]>();
    timelineDated.forEach((i) => {
      if (!i.parentInitiativeId) return;
      const siblings = map.get(i.parentInitiativeId) || [];
      siblings.push(i);
      map.set(i.parentInitiativeId, siblings);
    });
    map.forEach((siblings) => siblings.sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [timelineDated]);
  // See OverviewPage.tsx's own `rows` for the full reasoning on root selection per tier.
  const timelineRows = useMemo(() => {
    const roots = tierFilter === 'All'
      ? timelineDated.filter((i) => !i.parentInitiativeId || !timelineDatedIds.has(i.parentInitiativeId))
      : timelineDated.filter((i) => i.tier === tierFilter);
    const sortedRoots = tierFilter === 'Project'
      ? [...roots].sort((a, b) =>
          groupBy === 'category' ? a.category.localeCompare(b.category) : personName(a.ownerId).localeCompare(personName(b.ownerId))
        )
      : [...roots].sort((a, b) => a.name.localeCompare(b.name));
    return flattenTierTree(sortedRoots, timelineChildrenOf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineDated, timelineDatedIds, timelineChildrenOf, tierFilter, groupBy, people]);

  // Plain per-render computation, same reasoning as reportsByInitiative above — cheap for a
  // handful of initiatives, no memo needed.
  const horizonItems: { milestone: Milestone; initiative: Initiative }[] = [];
  initiatives.forEach((init) => {
    (init.milestones || []).forEach((m) => {
      if (m.status !== 'Done' && m.targetDate) horizonItems.push({ milestone: m, initiative: init });
    });
  });
  const horizon = horizonItems.sort((a, b) => a.milestone.targetDate.localeCompare(b.milestone.targetDate)).slice(0, 6);

  const recentReportItems: { report: WeeklyReport; initiative: Initiative }[] = [];
  initiatives.forEach((init) => {
    (reportsByInitiative[init.initiativeId] || []).slice(0, 3).forEach((r) => recentReportItems.push({ report: r, initiative: init }));
  });
  const recentReports = recentReportItems.sort((a, b) => b.report.createdAt.localeCompare(a.report.createdAt)).slice(0, 6);

  const donutSeries = [onTrack, atRisk, notStarted];
  const donutLabels = ['On track', 'At risk / blocked', 'Not started'];
  const donutColors = ['var(--bs-success)', 'var(--bs-warning)', 'var(--bs-secondary)'];

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-flashlight-line"
        title="Initiative Tracker"
        subtitle="Initiative status, read-only — for full editing, open an initiative in the Tracker"
        breadcrumbs={[{ label: 'Hub' }, { label: 'Initiative Tracker' }]}
      />

      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
        </div>
      ) : error ? (
        <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load the Initiative Tracker.</div>
      ) : initiatives.length === 0 ? (
        <div className="np-card">
          <div className="np-card-body py-5 text-center">
            <i className="ri-flashlight-line" style={{ fontSize: '3rem', color: 'var(--bs-secondary-color)' }}></i>
            <h6 className="mt-3 np-sub">No initiatives tracked yet.</h6>
          </div>
        </div>
      ) : (
        <>
          <div className="np-grid np-grid-4">
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-primary"><i className="ri-rocket-line"></i></span>
                <p className="np-stat-label">Active initiatives</p>
                <p className="np-stat-value np-tabular">{active.length}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-success"><i className="ri-checkbox-circle-line"></i></span>
                <p className="np-stat-label">On track</p>
                <p className="np-stat-value np-tabular">{onTrack}</p>
              </div>
            </div>
            <div className={`np-card ${atRisk > 0 ? 'np-card-accent-warning' : ''}`}>
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-warning"><i className="ri-alert-line"></i></span>
                <p className="np-stat-label">At risk / blocked</p>
                <p className="np-stat-value np-tabular">{atRisk}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <span className="np-stat-icon np-tone-neutral"><i className="ri-stack-line"></i></span>
                <p className="np-stat-label">Total tracked</p>
                <p className="np-stat-value np-tabular">{projectInitiatives.length}</p>
              </div>
            </div>
          </div>

          <div className="np-grid np-grid-8-4">
            <div className="np-card">
              <div className="np-card-head" style={{ flexWrap: 'wrap', rowGap: '0.5rem' }}>
                <h6>Timeline</h6>
                <div className="d-flex gap-2 flex-wrap" style={{ marginLeft: 'auto' }}>
                  {tierFilter === 'Project' && (
                    <div className="np-pill-toggle-group" role="group">
                      <button className={`np-pill-toggle np-tone-primary${groupBy === 'category' ? ' active' : ''}`} onClick={() => setGroupBy('category')}>By category</button>
                      <button className={`np-pill-toggle np-tone-primary${groupBy === 'owner' ? ' active' : ''}`} onClick={() => setGroupBy('owner')}>By owner</button>
                    </div>
                  )}
                  <div className="np-pill-toggle-group" role="group">
                    {TIER_FILTERS.map((t) => (
                      <button key={t} className={`np-pill-toggle np-tone-primary${tierFilter === t ? ' active' : ''}`} onClick={() => setTierFilter(t)}>{t}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="np-card-body">
                {timelineRows.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="ri-bar-chart-2-line d-block mb-2" style={{ fontSize: '1.75rem', color: 'var(--bs-secondary-color)' }}></i>
                    <p className="np-sub mb-0">
                      {tierFilter === 'All'
                        ? 'No initiatives with both a start date and a projected end date yet.'
                        : `No ${tierFilter} initiatives with both a start date and a projected end date yet.`}
                    </p>
                  </div>
                ) : (
                  <Chart
                    type="rangeBar"
                    height={Math.max(120, timelineRows.length * 52)}
                    series={[{
                      data: timelineRows.map(({ initiative: i, depth }) => ({
                        x: indentLabel(i.name, depth),
                        y: [new Date(i.startDate).getTime(), new Date(i.currentProjectedEndDate || i.originalTargetEndDate).getTime()],
                      })),
                    }]}
                    options={{
                      chart: { type: 'rangeBar', toolbar: { show: false }, animations: { enabled: false } },
                      plotOptions: { bar: { horizontal: true, distributed: true, barHeight: '55%', rangeBarGroupRows: true } },
                      colors: timelineRows.map(({ initiative: i }) => HEALTH_HEX[effectiveHealth(i)] || 'var(--bs-secondary)'),
                      xaxis: { type: 'datetime', labels: { style: { colors: 'var(--bs-secondary-color)' } } },
                      yaxis: { labels: { style: { colors: 'var(--bs-secondary-color)', fontSize: '12.5px' } } },
                      grid: { borderColor: 'var(--bs-border-color)' },
                      dataLabels: { enabled: false },
                      legend: { show: false },
                      tooltip: {
                        custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
                          const { initiative: i, depth } = timelineRows[dataPointIndex];
                          return `<div style="padding:8px 12px;font-size:12.5px;">
                            <strong>${i.name}</strong>${i.tier ? ` · ${i.tier}` : ''}${depth > 0 ? ' (nested)' : ''}<br/>
                            ${effectiveHealth(i)} · ${i.percentComplete}% complete<br/>
                            ${formatDate(i.startDate)} → ${formatDate(i.currentProjectedEndDate)}
                          </div>`;
                        },
                      },
                      annotations: {
                        xaxis: [{
                          x: todayTs,
                          borderColor: 'var(--bs-danger)',
                          label: { text: 'Today', style: { color: '#fff', background: 'var(--bs-danger)', fontSize: '10px' } },
                        }],
                      },
                    }}
                  />
                )}
                <div className="np-legend">
                  <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: 'var(--bs-success)' }}></span>On track</div>
                  <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: 'var(--bs-warning)' }}></span>At risk</div>
                  <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: 'var(--bs-danger)' }}></span>Blocked</div>
                  <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: 'var(--bs-secondary)' }}></span>Not started</div>
                </div>
              </div>
            </div>

            <div className="np-card">
              <div className="np-card-head"><h6>Overall health</h6></div>
              <div className="np-card-body">
                <Chart
                  type="donut"
                  height={200}
                  series={donutSeries}
                  options={{
                    labels: donutLabels,
                    colors: donutColors,
                    legend: { position: 'bottom', fontSize: '11.5px', labels: { colors: 'var(--bs-body-color)' } },
                    dataLabels: { enabled: false },
                    stroke: { width: 0 },
                    plotOptions: { pie: { donut: { labels: { show: true, total: { show: true, label: 'Tracked', color: 'var(--bs-secondary-color)' } } } } },
                  }}
                />
              </div>
            </div>
          </div>

          <div className="np-card">
            <div className="np-card-head">
              <h6>Initiatives</h6>
              <span className="np-sub">Click a row to expand — progress, risks, latest report</span>
            </div>
            <div className="np-card-body">
              <div className="hub-overview-list">
                {projectInitiatives.map((init) => (
                  <HubInitiativeCard
                    key={init.initiativeId}
                    initiative={init}
                    isOpen={!!expanded[init.initiativeId]}
                    onToggle={() => setExpanded((prev) => ({ ...prev, [init.initiativeId]: !prev[init.initiativeId] }))}
                    reports={reportsByInitiative[init.initiativeId] || []}
                    personName={personName}
                  />
                ))}
              </div>
            </div>
          </div>

          {bauInitiatives.length > 0 && (
            <div className="np-card">
              <div className="np-card-head">
                <h6>Standalone Work</h6>
                <span className="np-sub">Ongoing standing work — no target date, tracked separately from projects</span>
              </div>
              <div className="np-card-body">
                <div className="hub-overview-list">
                  {bauInitiatives.map((init) => (
                    <HubInitiativeCard
                      key={init.initiativeId}
                      initiative={init}
                      isOpen={!!expanded[init.initiativeId]}
                      onToggle={() => setExpanded((prev) => ({ ...prev, [init.initiativeId]: !prev[init.initiativeId] }))}
                      reports={reportsByInitiative[init.initiativeId] || []}
                      personName={personName}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="np-grid np-grid-8-4">
            <div className="np-card">
              <div className="np-card-head"><h6>Milestones on the horizon</h6></div>
              <div className="np-card-body">
                {horizon.length === 0 ? (
                  <p className="np-sub mb-0 fst-italic">Nothing upcoming — every tracked milestone is done.</p>
                ) : horizon.map(({ milestone, initiative }) => (
                  <div className="np-meta-row" key={`${initiative.initiativeId}-${milestone.id}`}>
                    <span className="np-meta-label"><i className="ri-flag-line"></i>{formatDate(milestone.targetDate)} · {milestone.name}</span>
                    <span className="np-meta-value">{initiative.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-head"><h6>Recent reports</h6></div>
              <div className="np-card-body">
                {recentReports.length === 0 ? (
                  <p className="np-sub mb-0 fst-italic">No weekly reports submitted yet.</p>
                ) : recentReports.map(({ report, initiative }) => (
                  <div className="np-meta-row" key={report.reportId}>
                    <span className="np-meta-label">
                      <span className={`np-chip np-tone-${toneOf(HEALTH_COLOR[report.health])}`} style={{ marginRight: '0.4rem' }}>{report.health}</span>
                      {initiative.name}
                    </span>
                    <span className="np-meta-value np-tabular">{fmtRelative(report.createdAt)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const HubInitiativeCard: React.FC<{
  initiative: Initiative;
  isOpen: boolean;
  onToggle: () => void;
  reports: WeeklyReport[];
  personName: (id: string) => string;
}> = ({ initiative, isOpen, onToggle, reports, personName }) => {
  const pct = milestoneCompletion(initiative.milestones || []) ?? initiative.percentComplete;
  const openRisks = (initiative.risks || []).filter((r) => r.status !== 'Closed');
  const latestReport = reports[0];
  const latestException = [...(initiative.exceptions || [])].reverse()[0];

  return (
    <div className="hub-overview-card">
      <div className="hub-overview-row">
        <button
          type="button"
          className="np-icon-btn flex-shrink-0"
          title={isOpen ? 'Collapse' : 'Expand — progress, risks, latest report'}
          onClick={onToggle}
        >
          <i className={isOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}></i>
        </button>
        <div className="flex-grow-1 min-width-0">
          <span className="hub-overview-title text-truncate d-block">{initiative.name}</span>
          <span className={`np-chip np-tone-${toneOf(CATEGORY_COLOR[initiative.category] || 'secondary')}`}>{initiative.category}</span>
        </div>
        <span className={`np-chip np-tone-${toneOf(HEALTH_COLOR[effectiveHealth(initiative)])} flex-shrink-0`}>{effectiveHealth(initiative)}</span>
        <span className={`np-chip np-tone-${toneOf(STATUS_COLOR[initiative.status])} flex-shrink-0 d-none d-md-inline-flex`}>{initiative.status}</span>
        <div className="d-none d-md-flex align-items-center gap-2 flex-shrink-0" style={{ width: '120px' }}>
          <div className="np-progress" style={{ flex: 1 }}><div className="np-fill" style={{ width: `${pct}%`, background: HEALTH_HEX[effectiveHealth(initiative)] }}></div></div>
          <span className="np-tabular np-sub" style={{ fontSize: '11.5px' }}>{pct}%</span>
        </div>
        <button
          type="button"
          className={`hub-overview-expand-btn flex-shrink-0 ${isOpen ? 'open' : ''}`}
          title={isOpen ? 'Hide details' : 'Show progress, risks, and latest report'}
          onClick={onToggle}
        >
          <i className="ri-arrow-down-s-line"></i>
        </button>
      </div>

      {isOpen && (
        <>
          <div className="hub-overview-details">
            <div className="np-row-card mb-0">
              <div className="hub-overview-subcard-head"><i className="ri-flag-line"></i>Milestones</div>
              {(initiative.milestones || []).length === 0 ? (
                <p className="np-sub mb-0 fst-italic">No milestones scheduled yet.</p>
              ) : (
                initiative.milestones.slice(0, 5).map((m) => (
                  <div className="np-meta-row" key={m.id}>
                    <span className="np-meta-label text-truncate">{m.name}{m.weight ? ` · ${m.weight}%` : ''}</span>
                    <span className={`np-chip np-tone-${m.status === 'Done' ? 'success' : m.status === 'Missed' ? 'danger' : m.status === 'At Risk' ? 'warning' : 'neutral'}`}>{m.status}</span>
                  </div>
                ))
              )}
              {latestException && (
                <span className={`np-chip np-tone-${EXCEPTION_STATUS_TONE[latestException.status]}`} style={{ marginTop: '0.5rem', display: 'inline-flex' }}>
                  <i className="ri-history-line"></i>Schedule exception · {latestException.status}
                </span>
              )}
            </div>

            <div className="np-row-card mb-0">
              <div className="hub-overview-subcard-head"><i className="ri-error-warning-line"></i>Risks</div>
              {openRisks.length === 0 ? (
                <p className="np-sub mb-0 fst-italic">No open risks tracked on this initiative.</p>
              ) : (
                <>
                  {openRisks.slice(0, 3).map((r) => (
                    <div className="np-meta-row" key={r.id}>
                      <span className="np-meta-label text-truncate">{r.description}</span>
                      <span className={`np-chip np-tone-${RISK_SEVERITY_TONE[r.severity]}`}>{r.severity}</span>
                    </div>
                  ))}
                  {openRisks.length > 3 && (
                    <p className="np-sub mb-0" style={{ marginTop: '0.35rem' }}>{openRisks.length} open · 3 shown</p>
                  )}
                </>
              )}
            </div>

            <div className="np-row-card mb-0">
              <div className="hub-overview-subcard-head"><i className="ri-file-list-3-line"></i>Latest report</div>
              {!latestReport ? (
                <p className="np-sub mb-0 fst-italic">No weekly reports submitted yet.</p>
              ) : (
                <>
                  <p className="np-sub mb-0">
                    <span className={`np-chip np-tone-${toneOf(HEALTH_COLOR[latestReport.health])}`} style={{ marginRight: '0.4rem' }}>{latestReport.health}</span>
                    {latestReport.percentComplete}% complete · {fmtRelative(latestReport.createdAt)}
                  </p>
                  <p className="np-sub mb-0" style={{ marginTop: '0.35rem' }}>
                    Latest by {personName(latestReport.submittedBy)} · {reports.length} weekly report{reports.length === 1 ? '' : 's'} in total.
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="px-3 pb-3">
            <Link to={`/tracker/initiatives/${initiative.initiativeId}`} className="np-btn np-btn-ghost np-btn-sm">
              <i className="ri-external-link-line"></i>View full initiative in Tracker
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default HubInitiativeTracker;
